import { portalText } from "../i18n/portal.js";
/**
 * Where a person may go — the pure rules behind the pickers, the landing, deep links and the
 * revoked-access fallback (plan-014 A4; design §27.6.3; FR12-AUTH-10/12).
 *
 * Every rule here reads the auth_session's FRESH access listing (`accountChoice`, re-read from
 * `account_access_for_person()` with every auth_session body), never a remembered one: a last-used
 * workspace, a bookmarked page or a search result is only a hint, re-checked against what the person
 * can enter now. Nothing here allows anything — the API decides every act against its target — it
 * decides what to OFFER and where to SEND, so a person is never sent to a page they will be refused.
 */
import { holdsAccountCapability, type AccountCapability } from "@wetalk/domain";

import type { AccountChoiceView, AuthSessionView, ScopeTargetView, WorkspaceChoiceView } from "../api/gateway.js";

/** The account capabilities an organization role holds there, from the listing the API sent. */
function holds(authSession: AuthSessionView, capability: AccountCapability): boolean {
  return authSession.capability.account.includes(capability);
}

/** The workspaces of an organization the person can open: archived ones are never offered. */
export function enterableWorkspaceOf(choice: AccountChoiceView): readonly WorkspaceChoiceView[] {
  return choice.workspace.filter((one) => one.enterable);
}

/**
 * Whether an organization role in `choice` reaches the money. The context's own capability listing
 * covers only the CURRENT organization, so for another one the role is asked of the canonical
 * evaluator's matrix (`@wetalk/domain`, design §27.5.2), never restated here.
 */
function roleReachesFinance(choice: AccountChoiceView): boolean {
  return choice.accountRole !== null && holdsAccountCapability(choice.accountRole, "account.finance.read");
}

/**
 * Where picking an organization goes, or `null` when there is nowhere in it the person may open.
 *
 *   · a closed organization: nowhere;
 *   · a suspended one: its Finance pages for someone who holds its money (to settle it), else nowhere
 *     (design §27.6.3 "suspended account → /finance/funding for finance holders");
 *   · otherwise the last-used workspace if it is still enterable, else the first enterable one, else
 *     the organization alone for someone with an organization role there.
 */
export function defaultScopeTargetOf(choice: AccountChoiceView, lastUsedWorkspaceId: string | null): ScopeTargetView | null {
  if (choice.state === "closed") return null;
  if (choice.state === "suspended") {
    return roleReachesFinance(choice) ? { accountId: choice.accountId, workspaceId: null } : null;
  }
  const enterable = enterableWorkspaceOf(choice);
  const lastUsed = enterable.find((one) => one.workspaceId === lastUsedWorkspaceId);
  const workspace = lastUsed ?? enterable[0];
  if (workspace !== undefined) return { accountId: choice.accountId, workspaceId: workspace.workspaceId };
  if (choice.accountRole !== null) return { accountId: choice.accountId, workspaceId: null };
  return null;
}

/** Why an organization offers nowhere to go, in plain words — `null` when it does. */
export function unreachableReasonOf(choice: AccountChoiceView): string | null {
  if (choice.state === "closed") return portalText("shell", "this_organization_is_closed_3bded318");
  if (choice.state === "suspended" && !roleReachesFinance(choice)) {
    return portalText("shell", "this_organization_is_suspended_the_people_who_manage_its_m_84059136");
  }
  if (defaultScopeTargetOf(choice, null) === null) return portalText("shell", "none_of_its_workspaces_can_be_opened_right_now_3fe169f0");
  return null;
}

/** The console path a context lands on (design §27.6.3), or `null` for the organization picker. */
export function landingPathOf(authSession: AuthSessionView): string | null {
  if (authSession.kind === "selection_required") return null;
  if (authSession.kind === "workspace_selected") return "/dashboard";
  return authSession.landing === "finance" || !holds(authSession, "account.profile.read") ? "/finance/funding" : "/organization";
}

/**
 * Pages that belong to no scope, so open in every context: the person's own (My security) and the
 * documentation, which reads nothing of an organization.
 */
const PERSONAL_PREFIX = ["/me", "/docs"] as const;

function underPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

/**
 * Whether `pathname` may be shown in this context — the deep-link re-check (FR12-AUTH-10).
 *
 *   · Organization pages need an organization role that reads the organization (Organization → Workspaces also
 *     opens with a workspace selected); Finance pages one that
 *     reads its money (the context's own capability listing); the audit log, with the organization alone open,
 *     one that lists an audit capability (INT for C4b, 2026-10-06);
 *   · every other page behind the shell is a workspace page, shown only with a workspace selected;
 *   · the person's own pages are shown everywhere.
 */
export function pathAllowedIn(authSession: AuthSessionView, pathname: string): boolean {
  if (PERSONAL_PREFIX.some((prefix) => underPrefix(pathname, prefix))) return true;
  if (authSession.kind === "selection_required") return false;
  // Design sync 2026-10-09: Organization → Workspaces lists the workspaces a person belongs to, so it opens with a
  // workspace selected even for someone with no organization role (who sees that tab alone).
  if (underPrefix(pathname, "/organization/workspaces")) {
    return authSession.kind === "workspace_selected" || holds(authSession, "account.profile.read");
  }
  if (underPrefix(pathname, "/organization")) return holds(authSession, "account.profile.read");
  if (underPrefix(pathname, "/finance")) return holds(authSession, "account.finance.read");
  // plan-014 INT for C4b (2026-10-06): the audit log opens with the organization alone too — the organization's log,
  // for a role that lists an audit capability (Finance: its financial entries only).
  if (underPrefix(pathname, "/audit") && authSession.kind === "account_only") {
    return holds(authSession, "account.audit.read") || holds(authSession, "account.audit.financial_read");
  }
  return authSession.kind === "workspace_selected";
}

/** The organization a context is in, from its fresh access listing — `undefined` when none. */
export function currentChoiceOf(authSession: AuthSessionView): AccountChoiceView | undefined {
  if (authSession.kind === "selection_required") return undefined;
  return authSession.accountChoice.find((one) => one.accountId === authSession.accountId);
}
